import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaRobot, FaSearch, FaArrowRight, FaPlus, FaVial, FaHistory } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import './AgentList.css';

const AgentList = () => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        const response = await api.getAgents();
        if (response && response.data) {
          setAgents(response.data);
          setFilteredAgents(response.data);
        } else {
          throw new Error('No data received from server');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching agents:', err);
        setError('Failed to load agents. Please try again later.');
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const filtered = agents.filter(
        agent => 
          agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAgents(filtered);
    }
  }, [searchTerm, agents]);

  const handleAgentSelect = (agentId) => {
    navigate(`/agents/${agentId}`);
  };

  const handleDevelop = (agent, e) => {
    e.stopPropagation();
    navigate(`/agents/${agent.id}`);
  };

  const handleTest = (agent, e) => {
    e.stopPropagation();
    navigate(`/agents/${agent.id}/testing`);
  };

  const handleLogs = (agent, e) => {
    e.stopPropagation();
    navigate(`/agents/${agent.id}/logs`);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner />
        <p className="mt-3">Loading agents...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4 main-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Agent List</h1>
        <Button 
          variant="primary" 
          onClick={() => navigate('/agents/add')}
        >
          <FaPlus className="me-2" /> Add New Agent
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="mb-4">
        <Form.Group className="search-container">
          <div className="input-group">
            <span className="input-group-text">
              <FaSearch />
            </span>
            <Form.Control
              type="text"
              placeholder="Search agents by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </Form.Group>
      </div>

      {filteredAgents.length === 0 ? (
        <div className="text-center p-5 bg-light rounded">
          {searchTerm ? (
            <>
              <p>No agents found matching your search criteria.</p>
              <Button variant="outline-primary" onClick={() => setSearchTerm('')}>
                Clear Search
              </Button>
            </>
          ) : (
            <>
              <p>No agents available. Start by adding a new agent.</p>
              <Button variant="primary" onClick={() => navigate('/agents/add')}>
                Add New Agent
              </Button>
            </>
          )}
        </div>
      ) : (
        <Row>
          {filteredAgents.map(agent => (
            <Col key={agent.id} md={6} lg={4} className="mb-4">
              <Card 
                className="agent-card h-100"
                onClick={() => handleAgentSelect(agent.id)}
              >
                <Card.Body className="d-flex flex-direction-column">
                  <div className="d-flex mb-3">
                    <div className="agent-icon">
                      <FaRobot size={24} />
                    </div>
                    <div className="ms-3">
                      <h3>{agent.name || 'Unnamed Agent'}</h3>
                    </div>
                  </div>
                  <Card.Text className="flex-grow-1">
                    {agent.description || 'No description available'}
                  </Card.Text>
                  <div className="agent-meta">
                    <span className="agent-type">{agent.type || 'Agent'}</span>
                    <span 
                      className="agent-status" 
                      data-status={agent.status?.toLowerCase() || 'inactive'}
                    >
                      {agent.status || 'Inactive'}
                    </span>
                  </div>
                  
                  {/* FIXED: Single row action buttons */}
                  <div className="agent-actions-row">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="action-btn"
                      onClick={(e) => handleDevelop(agent, e)}
                    >
                      Develop
                    </Button>
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      className="action-btn"
                      onClick={(e) => handleLogs(agent, e)}
                    >
                      Logs
                    </Button>
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      className="action-btn"
                      onClick={(e) => handleTest(agent, e)}
                    >
                      Test
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default AgentList;